import type { ApolloError } from '@apollo/client'
import { useMoiaErrorNotification } from '@backoffice-frontend/patterns'
import { useMetricTracking } from '@backoffice-frontend/tracking'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFccRequestCabinVideoStreamCredentialsMutation } from './fccRequestCabinVideoStreamCredentials.hook'

const isChimeNotSupportedError = (error: ApolloError) =>
  error?.graphQLErrors?.some(
    error => error.extensions?.code === 'CHIME_NOT_SUPPORTED',
  )

export const useChimeCredentials = (vehicleId: string | undefined) => {
  const { trackMetric } = useMetricTracking()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()
  const { t } = useTranslation()
  const [requestCabinVideoStreamCredentials, { loading, data }] =
    useFccRequestCabinVideoStreamCredentialsMutation({
      onError: error => {
        if (isChimeNotSupportedError(error)) {
          // The request can fail if Chime is not supported by that vehicle.
          console.info('Chime is not supported for this vehicle', {
            vehicleId,
            error,
          })
          return
        }
        enqueueMoiaErrorNotification(
          t('adFleetMap_videoStreamErrorNotRetrieved_caption'),
        )
      },
    })
  const credentials = data?.fccRequestCabinVideoStreamCredentials

  useEffect(() => {
    if (vehicleId) {
      requestCabinVideoStreamCredentials({
        variables: { input: { vehicleId } },
      })
      void trackMetric({
        metricName: 'ChimeCredentialsRequested',
        value: 1,
        dimensions: [{ name: 'vehicleId', value: vehicleId }],
        namespace: 'FleetControl/ad-operator-video',
      })
    }
  }, [vehicleId, requestCabinVideoStreamCredentials, trackMetric])

  return {
    credentials,
    isChimeAvailable: !loading && credentials,
    loading,
  }
}
